(ns rothko.dom.selection
  (:refer-clojure :exclude [set])
  (:require [clojure.string :as str]
            [rothko.dom.input :as input]
            [camel-snake-kebab.core :as csk]
            [utility-belt.pure :refer-macros [while-let]]))

(extend-type js/Selection
  ILookup
  (-lookup [coll k]
    (aget coll (csk/->camelCase (name k) ))))

(defn get-selection
  []
  (.. js/document getSelection))

(defn make-range
  []
  (.createRange js/document))

(defn text-node?
  [node]
  (try
    (= (.-TEXT_NODE js/Node)
       (.-nodeType node))
    (catch js/Error e
      (js/console.log e))))

(defn get-offsets-relative-to
  "Given a DOM element that fully contains a selection return the
  start and end char offsets of the selection into the element"
  [element]
  ;; Assumes LTR text.
  (let [selection (.getSelection js/window)]

    (when (and
           ;; There is generally only one range after a user interacts with the page
           ;; https://developer.mozilla.org/en-US/docs/Web/API/Selection/rangeCount
           (= 1 (.-rangeCount selection))
           ;; This checks that the selection is fully contained
           ;; within the element. This is not the case when a user
           ;; clicks out of the element and needs to be guarded
           ;; against.
           (= (-> js/document (.-activeElement))
              (-> element (.-parentElement))))
      (let [range (.getRangeAt selection 0)
            selection-count (-> range (.toString) (count))
            clone (.cloneRange range)]
        (doto clone
          (.selectNodeContents element)
          (.setEnd (.-endContainer range)
                   (.-endOffset range)))
        (let [clone-count (-> clone (.toString) (count))]
          {:document-start-offset (- clone-count selection-count)
           :document-end-offset clone-count})))))

(defn- child-node-relative-offsets
  "Given two arguments; a DOM element that contains text nodes and
  start and end char offsets into the element, return the nodes that
  the document offsets are contained in and the remaining char offsets
  into the those nodes"
  [element {:keys [document-start-offset document-end-offset] :as _document-offsets}]
  ;; This function creates a DOM tree walker that filters only nodes
  ;; that contain text. We then start looping through the walker
  ;; testing whether either of the offsets are contained in the node.
  ;; The three possibilities are; both offsets are in the current
  ;; node, the start offset is or the end offset is. The fourth
  ;; document.
  (let [walker (.createTreeWalker js/document element (.-SHOW_TEXT js/NodeFilter) nil false)]
    (loop [current-offset 0
           selection-start nil]
      (let [node
            (.nextNode walker)

            total
            (+ current-offset (if node (.-length node) 0))

            no-text-nodes-in-element?
            (not node)

            selection-entirely-within-this-token?
            (and (>= total document-start-offset)
                 (>= total document-end-offset)
                 (not selection-start))

            have-selection-start-and-end-node-in-this-token?
            (and (>= total document-start-offset)
                 (>= total document-end-offset)
                 selection-start)

            only-selection-start-in-this-token?
            (and (>= total document-start-offset)
                 (< total document-end-offset)
                 (not selection-start))]
        (cond

          ;; Case 1
          ;; This case only applies to an empty document
          no-text-nodes-in-element?
          nil

          ;; Case 2
          ;; Case for when start and end document offsets are in the
          ;; same token. There is no partial result, we calculate the
          ;; document offsets at the same time and the node is the
          ;; same
          selection-entirely-within-this-token?
          {:start-node node
           :start-offset (- document-start-offset current-offset)
           :end-node node
           :end-offset (- document-end-offset current-offset)}

          ;; Case 3
          ;; This is second end case where the document offsets were
          ;; in separate words. The partial result that was collected
          ;; in the next case and here we add the end node and offset
          ;; to the result and return.
          have-selection-start-and-end-node-in-this-token?
          (merge selection-start
                 {:end-node node
                  :end-offset (- document-end-offset current-offset)})

          ;; Case 4
          ;; This intermediate case is triggered when we hit the word
          ;; that contains the start document offset but the end
          ;; offset is still ahead of us (if both the start and end
          ;; offsets were in the word we would trigger case 2). We
          ;; store the `start-node` and `start-offset` for later when
          ;; we hit case 3.
          only-selection-start-in-this-token?
          (recur total
                 {:start-node node
                  :start-offset (- document-start-offset current-offset)})

          ;; Case 5
          ;; This intermediate case occurs in two places in the
          ;; document; firstly, before we hit the start document
          ;; offset (but not when we hit it, that is case 4) and
          ;; secondly after we've hit the start document offset but
          ;; before we hit the end document offset (case 3).
          ;; `partial-res` is `nil` in the first range and contains
          ;; the `start-node` and `start-offset` in the second range
          :default
          (recur total
                 selection-start))))))

(defn apply-range
  [selection range]
  (doto selection
    (.removeAllRanges)
    (.addRange range))
  (.detach range))

(defn set
  [element element-offsets]
  (let [{:keys [start-node start-offset end-node end-offset]}
        (child-node-relative-offsets element element-offsets)

        selection
        (get-selection)

        range
        (make-range)]
    (when (and start-node end-node) ; TODO: Maybe this is just a symptom of a wider bug, but I've noticed that these can end up nil, so I've added this guard.
      (doto range
        (.setStart start-node start-offset)
        (.setEnd end-node end-offset)))
    (apply-range selection range)))

(defn expand-selection
  [start-element end-element]
  (let [selection (get-selection)
        range (make-range)]
    (doto range
      (.setStart start-element 0)
      (.setEnd end-element 1))
    (apply-range selection range)))
